/** @jsx jsx */
import { jsx } from 'theme-ui';

// HTML/CSS recreations of the NewsImpact iOS UI (dark appearance), based on the
// app's own design tokens and components (NewsCard, AI analysis panel, ConfidenceMeter).
// All content is illustrative sample data.

export const app = {
  bg: '#111315',
  card: '#1A1D20',
  elevated: '#24292E',
  border: '#343A40',
  text: '#F3F6FA',
  muted: '#9BA6B2',
  primary: '#47D18C',
  bear: '#FF827C',
  high: '#FFB968',
  blue: '#93B2BC',
};

const mono =
  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace';

/* ---------- icons ---------- */

export const Icon = ({ name, size = 14, color = 'currentColor', ...props }) => {
  const paths = {
    up: <path d="M5 11L11 5M11 5H6M11 5V10" />,
    down: <path d="M5 5L11 11M11 11H6M11 11V6" />,
    bolt: <path d="M9 1.5L3.5 9H8L7 14.5L12.5 7H8L9 1.5Z" />,
    star: (
      <path d="M8 1.8l1.9 3.9 4.3.6-3.1 3 .7 4.3L8 11.6l-3.8 2 .7-4.3-3.1-3 4.3-.6L8 1.8z" />
    ),
    news: (
      <path d="M2.5 3.5h9v9h-8a1 1 0 01-1-1v-8zM11.5 6h2v5.5a1 1 0 01-1 1M5 6h4M5 8.5h4M5 11h2.5" />
    ),
    gear: (
      <path d="M8 10.2a2.2 2.2 0 100-4.4 2.2 2.2 0 000 4.4zM8 1.5v2M8 12.5v2M1.5 8h2M12.5 8h2M3.4 3.4l1.4 1.4M11.2 11.2l1.4 1.4M3.4 12.6l1.4-1.4M11.2 4.8l1.4-1.4" />
    ),
    calendar: (
      <path d="M2.5 4h11v9.5h-11V4zM2.5 6.8h11M5.2 2.5v3M10.8 2.5v3" />
    ),
    check: <path d="M3 8.5l3 3 7-7" />,
    alert: (
      <path d="M8 2l6.5 11.5h-13L8 2zM8 6.5v3M8 11.6v.2" />
    ),
    clock: (
      <path d="M8 14A6 6 0 108 2a6 6 0 000 12zM8 4.8V8l2.2 1.6" />
    ),
    link: (
      <path d="M6.5 9.5l3-3M7 4.5l1-1a2.8 2.8 0 014 4l-1 1M9 11.5l-1 1a2.8 2.8 0 01-4-4l1-1" />
    ),
    arrow: <path d="M3 8h10M9 4l4 4-4 4" />,
  };
  const filled = name === 'bolt' || name === 'star';
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill={filled ? color : 'none'}
      stroke={filled ? 'none' : color}
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      {paths[name]}
    </svg>
  );
};

/* ---------- phone frame ---------- */

const StatusBar = () => (
  <div
    sx={{
      position: 'relative',
      height: 44,
      px: '26px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      color: app.text,
      fontSize: '13px',
      fontWeight: 600,
    }}
  >
    <span>9:41</span>
    <span
      sx={{
        position: 'absolute',
        left: '50%',
        top: 10,
        transform: 'translateX(-50%)',
        width: 92,
        height: 26,
        borderRadius: 20,
        bg: '#000',
      }}
    />
    <span sx={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
      <svg width="16" height="10" viewBox="0 0 16 10" fill={app.text} aria-hidden="true">
        <rect x="0" y="6" width="3" height="4" rx="1" />
        <rect x="4.3" y="4" width="3" height="6" rx="1" />
        <rect x="8.6" y="2" width="3" height="8" rx="1" />
        <rect x="12.9" y="0" width="3" height="10" rx="1" />
      </svg>
      <svg width="22" height="11" viewBox="0 0 22 11" aria-hidden="true">
        <rect x="0.5" y="0.5" width="18" height="10" rx="3" fill="none" stroke={app.muted} />
        <rect x="2" y="2" width="15" height="7" rx="1.6" fill={app.text} />
        <rect x="19.6" y="3.5" width="1.6" height="4" rx="0.8" fill={app.muted} />
      </svg>
    </span>
  </div>
);

export const Phone = ({ children, ...props }) => (
  <div
    {...props}
    sx={{
      position: 'relative',
      width: 300,
      height: 620,
      flex: '0 0 auto',
      borderRadius: 50,
      p: '9px',
      bg: '#050607',
      boxShadow:
        '0 0 0 1px rgba(255,255,255,0.14), inset 0 0 0 1px rgba(255,255,255,0.06), 0 50px 100px -30px rgba(0,0,0,0.9)',
    }}
  >
    <div
      sx={{
        position: 'relative',
        height: '100%',
        borderRadius: 42,
        overflow: 'hidden',
        bg: app.bg,
        fontFamily: 'inherit',
      }}
    >
      <StatusBar />
      {children}
    </div>
  </div>
);

/* ---------- small UI atoms ---------- */

export const TickerChip = ({ children }) => (
  <span
    sx={{
      fontFamily: mono,
      fontSize: '10px',
      fontWeight: 600,
      color: app.blue,
      bg: 'rgba(147,178,188,0.11)',
      px: '6px',
      py: '3px',
      borderRadius: 5,
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </span>
);

const impactColor = { critical: app.bear, high: app.high, medium: '#EBC15B', low: app.blue };
const impactLabel = { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low' };

const ImpactPill = ({ level }) => (
  <span
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '3px',
      fontSize: '10px',
      fontWeight: 600,
      color: impactColor[level],
      bg: `${impactColor[level]}1F`,
      px: '6px',
      py: '3px',
      borderRadius: 5,
    }}
  >
    <Icon name="bolt" size={9} color={impactColor[level]} />
    {impactLabel[level]}
  </span>
);

export const NewsCard = ({ source, time, title, ticker, impact, sentiment, lead }) => {
  const dot = sentiment === 'up' ? app.primary : sentiment === 'down' ? app.bear : app.blue;
  return (
    <div
      sx={{
        position: 'relative',
        bg: app.card,
        border: `1px solid ${lead ? `${impactColor[impact]}3D` : app.border}`,
        borderRadius: 10,
        p: lead ? '12px 12px 12px 15px' : '11px 12px',
        mb: '8px',
      }}
    >
      {lead && (
        <span
          sx={{
            position: 'absolute',
            left: 0,
            top: 12,
            bottom: 12,
            width: 3,
            borderRadius: 2,
            bg: impactColor[impact],
          }}
        />
      )}
      <div sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '6px' }}>
        <span sx={{ width: 6, height: 6, borderRadius: '50%', bg: dot }} />
        <span
          sx={{
            fontSize: '9.5px',
            fontWeight: 600,
            letterSpacing: '0.07em',
            color: app.muted,
          }}
        >
          {source}
        </span>
        <span sx={{ ml: 'auto', fontSize: '10px', color: app.muted }}>{time}</span>
      </div>
      <div
        sx={{
          fontSize: lead ? '14px' : '12.5px',
          fontWeight: lead ? 700 : 600,
          lineHeight: 1.3,
          color: app.text,
          mb: '8px',
        }}
      >
        {title}
      </div>
      <div sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <ImpactPill level={impact} />
        {sentiment !== 'flat' && (
          <Icon
            name={sentiment === 'up' ? 'up' : 'down'}
            size={12}
            color={sentiment === 'up' ? app.primary : app.bear}
          />
        )}
        <span sx={{ ml: 'auto' }}>
          <TickerChip>{ticker}</TickerChip>
        </span>
      </div>
    </div>
  );
};

/* ---------- feed screen (hero phone) ---------- */

const RoundButton = ({ icon }) => (
  <span
    sx={{
      width: 30,
      height: 30,
      borderRadius: '50%',
      bg: app.card,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <Icon name={icon} size={14} color={app.primary} />
  </span>
);

const FilterChip = ({ icon, color, children }) => (
  <span
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      fontSize: '10.5px',
      fontWeight: 600,
      color,
      border: `1px solid ${app.border}`,
      bg: app.card,
      px: '8px',
      py: '5px',
      borderRadius: 999,
      whiteSpace: 'nowrap',
    }}
  >
    <Icon name={icon} size={10} color={color} />
    {children}
  </span>
);

export const FeedScreen = () => (
  <div sx={{ px: '12px', color: app.text }}>
    <div sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: '6px', mb: '12px' }}>
      <img
        src="/newsimpact/app-icon.webp"
        alt=""
        width="30"
        height="30"
        sx={{ borderRadius: 8, bg: '#fff' }}
      />
      <div sx={{ lineHeight: 1.1 }}>
        <div sx={{ fontSize: '8.5px', letterSpacing: '0.18em', color: app.muted }}>
          NEWSIMPACT
        </div>
        <div sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em' }}>News</div>
      </div>
      <span sx={{ ml: 'auto', display: 'flex', gap: '6px' }}>
        <RoundButton icon="calendar" />
        <RoundButton icon="gear" />
      </span>
    </div>

    <div
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '4px',
        p: '3px',
        bg: app.card,
        borderRadius: 10,
        mb: '10px',
        fontSize: '11.5px',
        fontWeight: 600,
      }}
    >
      <span
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '5px',
          py: '7px',
          color: app.text,
        }}
      >
        <Icon name="news" size={12} color={app.text} /> All markets
      </span>
      <span
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '5px',
          py: '7px',
          borderRadius: 8,
          bg: app.primary,
          color: '#06210F',
        }}
      >
        <Icon name="star" size={11} color="#06210F" /> Watchlist
      </span>
    </div>

    <div sx={{ display: 'flex', gap: '5px', mb: '12px', overflow: 'hidden' }}>
      <FilterChip icon="bolt" color={app.high}>
        High impact
      </FilterChip>
      <FilterChip icon="up" color={app.primary}>
        Positive
      </FilterChip>
      <FilterChip icon="down" color={app.bear}>
        Negative
      </FilterChip>
    </div>

    <div sx={{ display: 'flex', alignItems: 'flex-end', mb: '8px' }}>
      <div>
        <div sx={{ fontSize: '10px', color: app.muted }}>Watchlist feed</div>
        <div sx={{ fontSize: '15px', fontWeight: 700 }}>Watchlist insights</div>
      </div>
      <span
        sx={{
          ml: 'auto',
          fontSize: '10px',
          color: app.muted,
          bg: app.card,
          px: '8px',
          py: '4px',
          borderRadius: 999,
        }}
      >
        12 stories
      </span>
    </div>

    <NewsCard
      lead
      source="KAP"
      time="2m"
      title="Material event disclosure: new long-term export agreement signed"
      ticker="DEMO"
      impact="high"
      sentiment="up"
    />
    <NewsCard
      source="MACRO"
      time="14m"
      title="Inflation print comes in below consensus"
      ticker="USDTRY"
      impact="critical"
      sentiment="up"
    />
    <NewsCard
      source="COMMODITIES"
      time="31m"
      title="Brent crude extends gains after supply update"
      ticker="BRENT"
      impact="medium"
      sentiment="flat"
    />
    <NewsCard
      source="CRYPTO"
      time="1h"
      title="Volatility rises ahead of key macro data"
      ticker="BTC"
      impact="low"
      sentiment="down"
    />
  </div>
);

/* ---------- AI analysis panel ---------- */

const PanelLabel = ({ children }) => (
  <div
    sx={{
      fontSize: '10px',
      fontWeight: 600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: app.muted,
      mb: '6px',
    }}
  >
    {children}
  </div>
);

export const ConfidenceMeter = ({ value, label, color = app.primary }) => (
  <div>
    <div sx={{ display: 'flex', alignItems: 'baseline', mb: '6px' }}>
      <span sx={{ fontSize: '12px', color: app.muted }}>Confidence</span>
      <span sx={{ ml: 'auto', fontSize: '13px', fontWeight: 700, color: app.text }}>
        {value}%
      </span>
    </div>
    <div sx={{ height: 6, borderRadius: 999, bg: app.elevated, overflow: 'hidden' }}>
      <div sx={{ width: `${value}%`, height: '100%', borderRadius: 999, bg: color }} />
    </div>
    {label && <div sx={{ mt: '6px', fontSize: '11px', color }}>{label}</div>}
  </div>
);

export const AIPanel = () => (
  <div
    sx={{
      bg: app.card,
      border: `1px solid ${app.border}`,
      borderRadius: 16,
      p: '16px',
      color: app.text,
      fontSize: '12.5px',
      lineHeight: 1.45,
      boxShadow: '0 30px 60px -30px rgba(0,0,0,0.8)',
    }}
  >
    <div sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '10px' }}>
      <span sx={{ fontWeight: 700, fontSize: '14px' }}>AI impact analysis</span>
      <span
        sx={{
          ml: 'auto',
          fontSize: '10px',
          fontWeight: 600,
          color: '#C7D2FE',
          bg: 'rgba(129,140,248,0.14)',
          px: '7px',
          py: '3px',
          borderRadius: 999,
        }}
      >
        Claude
      </span>
    </div>
    <div sx={{ fontWeight: 600, mb: '6px' }}>Brent crude extends gains after supply update</div>
    <div
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '4px 10px',
        fontSize: '10.5px',
        color: app.muted,
        mb: '14px',
      }}
    >
      <span>Source: Commodities RSS</span>
      <span>Published 09:32</span>
      <span>Analyzed 09:33</span>
    </div>

    <div
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '10px',
        mb: '12px',
      }}
    >
      <div sx={{ bg: app.bg, borderRadius: 10, p: '10px' }}>
        <PanelLabel>Sentiment</PanelLabel>
        <div sx={{ fontWeight: 700, color: app.primary }}>Bullish · +0.42</div>
        <div
          sx={{
            position: 'relative',
            height: 4,
            mt: '8px',
            borderRadius: 999,
            background: `linear-gradient(90deg, ${app.bear}, ${app.blue}, ${app.primary})`,
            opacity: 0.85,
          }}
        >
          <span
            sx={{
              position: 'absolute',
              left: '71%',
              top: '-3px',
              width: 10,
              height: 10,
              borderRadius: '50%',
              bg: app.text,
              transform: 'translateX(-50%)',
            }}
          />
        </div>
      </div>
      <div sx={{ bg: app.bg, borderRadius: 10, p: '10px' }}>
        <PanelLabel>Directional bias</PanelLabel>
        <div sx={{ fontWeight: 700 }}>Positive</div>
        <div sx={{ display: 'flex', gap: '3px', mt: '8px' }} role="img" aria-label="Strength 3 of 5">
          {[1, 2, 3, 4, 5].map((n) => (
            <span
              key={n}
              sx={{
                flex: 1,
                height: 4,
                borderRadius: 999,
                bg: n <= 3 ? app.primary : app.elevated,
              }}
            />
          ))}
        </div>
      </div>
    </div>

    <PanelLabel>Affected assets</PanelLabel>
    <div sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap', mb: '12px' }}>
      <TickerChip>BRENT</TickerChip>
      <TickerChip>XU100</TickerChip>
      <TickerChip>USDTRY</TickerChip>
    </div>

    <div sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', mb: '12px' }}>
      <div>
        <PanelLabel>Short term</PanelLabel>
        <div sx={{ color: app.muted }}>Higher volatility in energy-sensitive assets.</div>
      </div>
      <div>
        <PanelLabel>Long term</PanelLabel>
        <div sx={{ color: app.muted }}>Depends on whether supply constraints persist.</div>
      </div>
    </div>

    <PanelLabel>Key risks</PanelLabel>
    <ul sx={{ m: 0, mb: '14px', pl: '16px', color: app.muted }}>
      <li>Reversal if inventories rise</li>
      <li>Broader risk-off sentiment</li>
    </ul>

    <ConfidenceMeter value={68} label="Medium confidence" />
  </div>
);

/* ---------- notification ---------- */

export const Notification = ({ title, body, time = 'now', faded = false }) => (
  <div
    sx={{
      display: 'flex',
      gap: '10px',
      alignItems: 'flex-start',
      p: '10px 12px',
      borderRadius: 18,
      bg: faded ? 'rgba(36,41,46,0.55)' : 'rgba(36,41,46,0.92)',
      border: '1px solid rgba(255,255,255,0.08)',
      backdropFilter: 'blur(12px)',
      color: app.text,
      opacity: faded ? 0.6 : 1,
      boxShadow: faded ? 'none' : '0 20px 40px -20px rgba(0,0,0,0.8)',
    }}
  >
    <img
      src="/newsimpact/app-icon.webp"
      alt=""
      width="32"
      height="32"
      loading="lazy"
      sx={{ borderRadius: 8, bg: '#fff', flex: '0 0 auto' }}
    />
    <div sx={{ minWidth: 0, flex: 1 }}>
      <div sx={{ display: 'flex', fontSize: '11px', color: app.muted, mb: '1px' }}>
        <span sx={{ fontWeight: 600 }}>NewsImpact</span>
        <span sx={{ ml: 'auto' }}>{time}</span>
      </div>
      <div sx={{ fontSize: '12.5px', fontWeight: 600, lineHeight: 1.3 }}>{title}</div>
      <div sx={{ fontSize: '12px', color: app.muted, lineHeight: 1.35 }}>{body}</div>
    </div>
  </div>
);

/* ---------- source toggles ---------- */

export const Toggle = ({ on }) => (
  <span
    aria-hidden="true"
    sx={{
      position: 'relative',
      width: 34,
      height: 20,
      flex: '0 0 auto',
      borderRadius: 999,
      bg: on ? app.primary : app.elevated,
      transition: 'background-color .2s',
    }}
  >
    <span
      sx={{
        position: 'absolute',
        top: 2,
        left: on ? 16 : 2,
        width: 16,
        height: 16,
        borderRadius: '50%',
        bg: '#fff',
      }}
    />
  </span>
);

/* ---------- portfolio ---------- */

export const PortfolioRows = () => {
  const rows = [
    { asset: 'XU100', value: '42,180', pl: '+3.2%', up: true },
    { asset: 'XAUUSD', value: '18,450', pl: '+0.8%', up: true },
    { asset: 'BTC', value: '9,760', pl: '−1.4%', up: false },
  ];
  return (
    <div sx={{ fontSize: '12.5px', color: app.text }}>
      <div
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr auto auto',
          gap: '12px',
          fontSize: '10px',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: app.muted,
          pb: '6px',
        }}
      >
        <span>Asset</span>
        <span>Value</span>
        <span sx={{ minWidth: 48, textAlign: 'right' }}>P/L</span>
      </div>
      {rows.map((r) => (
        <div
          key={r.asset}
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr auto auto',
            gap: '12px',
            alignItems: 'center',
            py: '8px',
            borderTop: `1px solid ${app.border}`,
          }}
        >
          <span sx={{ fontFamily: mono, fontWeight: 600 }}>{r.asset}</span>
          <span sx={{ color: app.muted, fontVariantNumeric: 'tabular-nums' }}>{r.value}</span>
          <span
            sx={{
              minWidth: 48,
              textAlign: 'right',
              fontWeight: 700,
              fontVariantNumeric: 'tabular-nums',
              color: r.up ? app.primary : app.bear,
            }}
          >
            {r.pl}
          </span>
        </div>
      ))}
      <div sx={{ display: 'flex', gap: '8px', mt: '8px', fontSize: '11px', color: app.muted }}>
        <span sx={{ bg: app.bg, borderRadius: 6, px: '8px', py: '4px' }}>Stop 40,900</span>
        <span sx={{ bg: app.bg, borderRadius: 6, px: '8px', py: '4px' }}>Target 45,000</span>
      </div>
    </div>
  );
};
